import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient, { CourtFiling } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { 
  Gavel, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock,
  AlertCircle,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';

export default function PendingFilings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState<CourtFiling | null>(null);

  const { data: pendingFilings, isLoading, error } = useQuery({
    queryKey: ['pending-filings'],
    queryFn: () => apiClient.getPendingFilings()
  });

  console.log('Pending filings query:', { pendingFilings, isLoading, error });

  const reviewMutation = useMutation({
    mutationFn: async ({ filingId, action, notes }: { filingId: number; action: 'accept' | 'reject'; notes?: string }) => {
      return apiClient.reviewFiling(filingId, action, notes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-filings'] });
      setReviewingId(null);
      setShowRejectDialog(null);
      setRejectReason('');
    }
  });

  const handleAccept = (filing: CourtFiling) => {
    setReviewingId(filing.id);
    reviewMutation.mutate({ filingId: filing.id, action: 'accept' });
  };

  const handleReject = () => {
    if (!showRejectDialog) return;
    reviewMutation.mutate({ 
      filingId: showRejectDialog.id, 
      action: 'reject', 
      notes: rejectReason 
    });
  };

  // Only CaseManager and SystemAdmin can access this page
  if (user?.role !== 'CaseManager' && user?.role !== 'SystemAdmin') {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-900">Access Restricted</h2>
        <p className="text-gray-600 mt-2">Only Case Managers can review court filings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-100 rounded-lg">
            <Gavel className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pending Filings</h1>
            <p className="text-gray-600">Review and approve court document submissions</p>
          </div>
        </div>
        {pendingFilings && pendingFilings.length > 0 && (
          <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
            {pendingFilings.length} pending
          </span>
        )}
      </div>

      {/* Pending Filings List */}
      {isLoading ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading pending filings...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Failed to load filings</h3>
          <p className="text-gray-600 mt-1">{(error as Error).message}</p>
        </div>
      ) : !pendingFilings || pendingFilings.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">All caught up!</h3>
          <p className="text-gray-600 mt-1">No filings pending review.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
          {pendingFilings?.map((filing) => (
            <div key={filing.id} className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                    <FileText className="h-6 w-6 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{filing.title}</h3>
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                        {filing.filingType}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <Link 
                        to={`/cases/${filing.caseNumber}`}
                        className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                      >
                        {filing.caseNumber}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                      {filing.caseTitle && (
                        <>
                          <span>•</span>
                          <span>{filing.caseTitle}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-2">
                      <Clock className="h-4 w-4" />
                      <span>Filed by {filing.filedBy} ({filing.filedByRole})</span>
                      <span>•</span>
                      <span>{format(new Date(filing.filedAt), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                    {filing.description && (
                      <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                        {filing.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleAccept(filing)}
                    disabled={reviewingId === filing.id}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {reviewingId === filing.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    Accept
                  </button>
                  <button
                    onClick={() => setShowRejectDialog(filing)}
                    disabled={reviewingId === filing.id}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-red-600 text-sm font-medium rounded-lg border border-red-300 hover:bg-red-50 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reject Filing</h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              You are rejecting: <strong>{showRejectDialog.title}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for rejection (optional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Provide feedback to the filer..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectDialog(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={reviewMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {reviewMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                Reject Filing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
